import { Box } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import { isEmpty } from 'lodash'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

// TODO: Pending to migrate to graphql
import workAreas from 'modules/locations/store/collections/workAreas'
import useLocationOptions from 'modules/shift_management/hooks/useLocationOptions'
import { SYMBOLIC_MIXED_VALUE } from 'modules/shift_management/lib/types'

import TooltipIcon from './TooltipIcon'

interface Props {
  locationField: Field
  workAreaField: Field
  mixedValuesLocation?: boolean
  mixedValuesWorkArea?: boolean
  newVersion?: boolean
  showLocationField?: boolean
  disabledLocation?: boolean
  hideNoLocationOption?: boolean
}

const ShiftLocationPicker = ({
  locationField,
  workAreaField,
  mixedValuesLocation,
  mixedValuesWorkArea,
  newVersion = false,
  showLocationField = true,
  disabledLocation = false,
  hideNoLocationOption = false,
}: Props) => {
  useAsyncData(() => workAreas.fetch(), [])

  const { locationOptions, workAreaOptions } = useLocationOptions({
    locationField,
    mixedValuesLocation,
    mixedValuesWorkArea,
    hideNoLocationOption,
  })

  const readOnlyWorkAreas =
    !locationField.value ||
    (locationField.value && Number(locationField.value) === SYMBOLIC_MIXED_VALUE) ||
    isEmpty(workAreaOptions)

  return (
    <>
      <Box gap="s16" flexDirection="row" justifyContent="center" alignItems="center">
        {newVersion && (
          <TooltipIcon.Medium
            icon="pin"
            label={i18n.t('shift_management.actions_sidepanel.tooltip.workplace')}
            color="grey700"
          />
        )}
        {showLocationField && (
          <Box width="full">
            <DropdownSearchBar
              searchLabel={i18n.t('common.search')}
              sortLabel={i18n.t('common.name')}
              selectLabel={i18n.t('common.select')}
              labelNotFound={i18n.t('common.select')}
              label={newVersion ? '' : i18n.t('shift_management.add_shift.location')}
              data={locationOptions}
              name={(() => {
                const normalizedValue = !locationField.value ? 0 : locationField.value
                return (
                  locationOptions.find((option) => String(option.value) === String(normalizedValue))
                    ?.label || i18n.t('common.select')
                )
              })()}
              onSelect={(option) => {
                workAreaField.set('')
                if (option.value === 0) {
                  locationField.set('')
                } else {
                  locationField.set(option.value)
                }
              }}
              autofocus={false}
              disabled={disabledLocation}
            />
          </Box>
        )}
      </Box>

      <Box gap="s16" flexDirection="row" justifyContent="center" alignItems="center">
        {newVersion && (
          <TooltipIcon.Medium
            icon="space-control"
            label={i18n.t('shift_management.actions_sidepanel.tooltip.work_area')}
            color="grey700"
          />
        )}
        <Box width="full">
          <DropdownSearchBar
            searchLabel={i18n.t('common.search')}
            sortLabel={i18n.t('common.name')}
            selectLabel={i18n.t('common.select')}
            labelNotFound={i18n.t('common.select')}
            hideLabel={newVersion}
            label={i18n.t('shift_management.add_shift.work_area')}
            data={workAreaOptions}
            name={(() => {
              const normalizedValue = !workAreaField.value ? 0 : workAreaField.value
              return (
                workAreaOptions.find((option) => String(option.value) === String(normalizedValue))
                  ?.label || i18n.t('common.select')
              )
            })()}
            onSelect={(option) => {
              if (option.value === 0) {
                workAreaField.set('')
              } else {
                workAreaField.set(option.value)
              }
            }}
            disabled={readOnlyWorkAreas}
            autofocus={false}
          />
        </Box>
      </Box>
    </>
  )
}

export default observer(ShiftLocationPicker)
